import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity,
  Database,
  HardDrive,
  Cpu,
  MemoryStick,
  Cloud,
  Users,
  FileText,
  Lock,
  Loader2,
  RefreshCw,
  Trash2,
  Download,
  Bell,
  Send,
  ScanSearch,
  FileWarning,
  ExternalLink,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const humanBytes = (n) => {
  if (n === null || n === undefined) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let f = Number(n);
  let i = 0;
  while (f >= 1024 && i < units.length - 1) {
    f /= 1024;
    i++;
  }
  return i === 0 ? `${Math.round(f)} ${units[i]}` : `${f.toFixed(1)} ${units[i]}`;
};

const Bar = ({ pct, danger = 80, warn = 60 }) => {
  const p = Math.max(0, Math.min(100, pct || 0));
  let color = "bg-emerald-500";
  if (p >= danger) color = "bg-red-500";
  else if (p >= warn) color = "bg-amber-500";
  return (
    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
      <div className={`h-full ${color} transition-all`} style={{ width: `${p}%` }} />
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, accent = "blue" }) => {
  const accents = {
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    purple: "bg-purple-50 text-purple-700 border-purple-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
  };
  return (
    <div className={`p-4 rounded-xl border ${accents[accent]}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide opacity-80">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="mt-1 font-heading text-2xl font-bold text-gray-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
    </div>
  );
};

const AdminStatsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Time-series
  const [history, setHistory] = useState([]);
  const [historyHours, setHistoryHours] = useState(24);

  // Documents
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Alerts
  const [alertCfg, setAlertCfg] = useState({
    webhook_url: "",
    enabled: false,
    thresholds: { cpu_pct: 90, mem_pct: 90, disk_pct: 90 },
  });
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [testingAlert, setTestingAlert] = useState(false);

  // R2 metrics
  const [r2Metrics, setR2Metrics] = useState(null);

  // Eng-# re-scan (data cleanup)
  const [rescanBusy, setRescanBusy] = useState(false);
  const [rescanResult, setRescanResult] = useState(null);
  const [rescanConfirmOpen, setRescanConfirmOpen] = useState(false);

  // Orphan extractions
  const [orphans, setOrphans] = useState([]);
  const [orphansLoading, setOrphansLoading] = useState(false);
  const [orphanFilterDays, setOrphanFilterDays] = useState(0);
  const [orphanSelected, setOrphanSelected] = useState(new Set());
  const [orphanPurging, setOrphanPurging] = useState(false);

  // Activity log
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityUserFilter, setActivityUserFilter] = useState("");
  const [activityEnabled, setActivityEnabled] = useState(true);
  const [activityToggleBusy, setActivityToggleBusy] = useState(false);
  const [latencyHistory, setLatencyHistory] = useState([]);

  const fetchLatencyHistory = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/admin/activity/latency-history`, { params: { hours: 24 } });
      const buckets = (resp.data.buckets || []).map(b => ({
        // Short label for X-axis, e.g. "11:00"
        label: new Date(b.hour).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
        server_ms: b.server_ms || 0,
        client_ms: b.client_ms ?? null,
        count: b.count,
      }));
      setLatencyHistory(buckets);
    } catch (err) { console.error("Latency history fetch failed:", err); }
  }, []);

  const fetchActivity = useCallback(async (userFilter = "") => {
    setActivityLoading(true);
    try {
      const params = { limit: 200 };
      if (userFilter.trim()) params.user = userFilter.trim();
      const resp = await axios.get(`${API}/admin/activity`, { params });
      setActivity(resp.data.items || []);
    } catch (err) {
      console.error("Activity fetch failed:", err);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  const fetchActivitySettings = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/admin/activity/settings`);
      setActivityEnabled(!!resp.data.enabled);
    } catch (err) { console.error("Activity settings fetch failed:", err); }
  }, []);

  const toggleActivityLogging = async () => {
    setActivityToggleBusy(true);
    try {
      const resp = await axios.post(`${API}/admin/activity/settings`, { enabled: !activityEnabled });
      setActivityEnabled(!!resp.data.enabled);
      toast.success(resp.data.enabled ? "Activity logging enabled" : "Activity logging paused");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle activity logging");
    } finally {
      setActivityToggleBusy(false);
    }
  };

  const fetchStats = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      const response = await axios.get(`${API}/admin/db-stats`);
      setStats(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load stats");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const fetchHistory = useCallback(async (hours) => {
    try {
      const response = await axios.get(`${API}/admin/db-stats/history?hours=${hours}`);
      setHistory(response.data.samples || []);
    } catch (err) {
      // non-fatal
      console.warn("history fetch failed", err);
    }
  }, []);

  const fetchDocuments = useCallback(async () => {
    try {
      setDocsLoading(true);
      const response = await axios.get(`${API}/admin/documents`);
      setDocuments(response.data.documents || []);
    } catch (err) {
      toast.error("Failed to load documents");
    } finally {
      setDocsLoading(false);
    }
  }, []);

  const fetchAlertConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/alerts/config`);
      setAlertCfg({
        webhook_url: response.data.webhook_url || "",
        enabled: !!response.data.enabled,
        thresholds: response.data.thresholds || { cpu_pct: 90, mem_pct: 90, disk_pct: 90 },
      });
    } catch {
      // non-fatal
    }
  }, []);

  const fetchR2Metrics = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/r2-metrics?hours=24`);
      setR2Metrics(r.data);
    } catch {
      setR2Metrics({ available: false, reason: "Failed to load R2 metrics" });
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchDocuments();
    fetchAlertConfig();
    fetchHistory(historyHours);
    fetchR2Metrics();
    const initialUser = new URLSearchParams(window.location.search).get("activityUser") || "";
    if (initialUser) setActivityUserFilter(initialUser);
    fetchActivity(initialUser);
    fetchActivitySettings();
    fetchLatencyHistory();
    const t = setInterval(() => fetchStats(true), 15000);
    const h = setInterval(() => fetchHistory(historyHours), 60000);
    const r2t = setInterval(() => fetchR2Metrics(), 5 * 60000);
    const a = setInterval(() => { fetchActivity(activityUserFilter); fetchLatencyHistory(); }, 15000);
    return () => { clearInterval(t); clearInterval(h); clearInterval(r2t); clearInterval(a); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyHours]);

  // Documents helpers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === documents.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(documents.map(d => d.id)));
    }
  };
  const downloadDoc = async (doc) => {
    try {
      const r = await axios.get(`${API}/extraction/${doc.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || `extraction_${doc.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.status === 404 ? "PDF file missing — cannot download" : "Download failed");
    }
  };
  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} extraction${ids.length > 1 ? 's' : ''}?\nThis also removes any saved batches that reference them.`)) return;
    try {
      setBulkDeleting(true);
      const r = await axios.post(`${API}/admin/documents/bulk-delete`, { ids });
      toast.success(`Deleted ${r.data.deleted} extraction(s) and cascaded ${r.data.details.reduce((s, x) => s + x.batches_deleted, 0)} batch(es)`);
      setSelectedIds(new Set());
      fetchDocuments();
      fetchStats(true);
    } catch (err) {
      toast.error("Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const saveAlertConfig = async () => {
    try {
      setSavingAlerts(true);
      const r = await axios.put(`${API}/admin/alerts/config`, alertCfg);
      setAlertCfg({
        webhook_url: r.data.webhook_url,
        enabled: r.data.enabled,
        thresholds: r.data.thresholds,
      });
      toast.success("Alert settings saved");
    } catch (err) {
      toast.error("Failed to save alert settings");
    } finally {
      setSavingAlerts(false);
    }
  };
  const testAlertWebhook = async () => {
    try {
      setTestingAlert(true);
      const r = await axios.post(`${API}/admin/alerts/test`);
      if (r.data.ok) toast.success(`Webhook fired OK (${r.data.detail})`);
      else toast.error(`Webhook failed: ${r.data.detail}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Webhook test failed");
    } finally {
      setTestingAlert(false);
    }
  };

  const runRescan = async (dryRun) => {
    try {
      setRescanBusy(true);
      const r = await axios.post(`${API}/admin/rescan-eng-drawings`, { dry_run: dryRun });
      setRescanResult(r.data);
      if (dryRun) {
        toast.success(`Preview: ${r.data.total_changed} eng #(s) would change across ${r.data.per_extraction.length} extraction(s)`);
      } else {
        toast.success(`Applied: ${r.data.total_changed} eng #(s) updated (${r.data.total_cleared} cleared, ${r.data.total_corrected} corrected)`);
        setRescanConfirmOpen(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Re-scan failed");
    } finally {
      setRescanBusy(false);
    }
  };

  const fetchOrphans = async (days = orphanFilterDays) => {
    try {
      setOrphansLoading(true);
      const r = await axios.get(`${API}/admin/orphan-extractions`, {
        params: { older_than_days: days },
      });
      setOrphans(r.data.orphans || []);
      setOrphanSelected(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load orphan extractions");
    } finally {
      setOrphansLoading(false);
    }
  };

  const toggleOrphanSelect = (id) => {
    setOrphanSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleOrphanSelectAll = () => {
    if (orphanSelected.size === orphans.length) {
      setOrphanSelected(new Set());
    } else {
      setOrphanSelected(new Set(orphans.map((o) => o.extraction_id)));
    }
  };

  const purgeSelectedOrphans = async () => {
    if (orphanSelected.size === 0) return;
    const ids = Array.from(orphanSelected);
    if (!window.confirm(
      `Delete ${ids.length} orphan extraction(s)?\n\n` +
      `These have no batches referencing them — safe to remove. The stored PDFs will be deleted from R2 / local storage as well.`
    )) return;
    try {
      setOrphanPurging(true);
      const r = await axios.post(`${API}/admin/orphan-extractions/purge`, { ids });
      const skippedCount = r.data.skipped?.length || 0;
      const nfCount = r.data.not_found?.length || 0;
      let msg = `Deleted ${r.data.deleted_count} extraction(s)`;
      if (skippedCount) msg += ` · ${skippedCount} skipped (recently batched)`;
      if (nfCount) msg += ` · ${nfCount} not found`;
      toast.success(msg);
      fetchOrphans();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to purge orphans");
    } finally {
      setOrphanPurging(false);
    }
  };

  // Format chart series
  const chartData = useMemo(() => history.map(s => ({
    time: new Date(s.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    cpu: Math.round((s.cpu_pct || 0) * 10) / 10,
    mem: Math.round((s.mem_pct || 0) * 10) / 10,
    disk: Math.round((s.disk_pct || 0) * 10) / 10,
    db_mb: Math.round(((s.db_data_bytes || 0) / 1048576) * 10) / 10,
  })), [history]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center text-gray-500">No data</div>
      </div>
    );
  }

  const sys = stats.system || {};
  const proc = stats.process || {};
  const mongo = stats.mongo || {};
  const storage = stats.storage || {};
  const app = stats.app || {};

  // Threshold helpers
  const memPct = sys.memory?.percent || 0;
  const diskPct = sys.disk?.percent || 0;
  const cpuPct = Math.min(100, proc.cpu_percent || 0);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      <main className="flex-1 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl font-bold text-gray-900 flex items-center gap-3" data-testid="admin-stats-title">
                <Activity className="w-7 h-7 text-blue-600" />
                System & Database Health
              </h1>
              <p className="text-gray-600 mt-1 text-sm">
                Real-time snapshot of MongoDB, PDF storage and the backend process. Refreshes every 15s.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="secondary" className={stats.environment === "production" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                {stats.environment.toUpperCase()}
              </Badge>
              <Button variant="outline" onClick={() => fetchStats()} disabled={refreshing} data-testid="refresh-btn">
                {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Refresh
              </Button>
            </div>
          </div>

          {/* App-level KPIs */}
          <section>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">App Data</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard icon={Users} label="Users" value={app.users} accent="purple" />
              <StatCard icon={FileText} label="Extractions" value={app.extractions} accent="blue" />
              <StatCard icon={Database} label="Batches" value={app.project_sheets} accent="emerald" />
              <StatCard icon={Lock} label="Locked Drawings" value={app.locked_drawings} accent="amber" />
            </div>
          </section>

          {/* Hardware */}
          <section>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Hardware (container)</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-testid="hw-cpu">
                <div className="flex items-center gap-2 text-gray-600">
                  <Cpu className="w-4 h-4" />
                  <span className="text-sm font-medium">CPU (this process)</span>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="font-heading text-3xl font-bold text-gray-900">{cpuPct.toFixed(1)}%</span>
                  <span className="text-xs text-gray-500">of one core</span>
                </div>
                <div className="mt-3">
                  <Bar pct={cpuPct} warn={70} danger={90} />
                </div>
                <div className="mt-3 text-xs text-gray-500 grid grid-cols-2 gap-2">
                  <span>Cores: <strong>{sys.cpu?.cores}</strong></span>
                  <span>Threads: <strong>{proc.threads}</strong></span>
                  {sys.cpu?.load_avg_1_5_15 && (
                    <span className="col-span-2">Load avg 1/5/15: <strong>{sys.cpu.load_avg_1_5_15.map((n) => n.toFixed(2)).join(" / ")}</strong></span>
                  )}
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-testid="hw-memory">
                <div className="flex items-center gap-2 text-gray-600">
                  <MemoryStick className="w-4 h-4" />
                  <span className="text-sm font-medium">Memory</span>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="font-heading text-3xl font-bold text-gray-900">{memPct.toFixed(1)}%</span>
                  <span className="text-xs text-gray-500">used</span>
                </div>
                <div className="mt-3">
                  <Bar pct={memPct} warn={70} danger={90} />
                </div>
                <div className="mt-3 text-xs text-gray-500 grid grid-cols-2 gap-2">
                  <span>Used: <strong>{humanBytes(sys.memory?.used_bytes)}</strong></span>
                  <span>Total: <strong>{humanBytes(sys.memory?.total_bytes)}</strong></span>
                  <span>Free: <strong>{humanBytes(sys.memory?.available_bytes)}</strong></span>
                  <span>Backend RSS: <strong>{humanBytes(proc.rss_bytes)}</strong></span>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-testid="hw-disk">
                <div className="flex items-center gap-2 text-gray-600">
                  <HardDrive className="w-4 h-4" />
                  <span className="text-sm font-medium">Disk (container)</span>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="font-heading text-3xl font-bold text-gray-900">{diskPct.toFixed(1)}%</span>
                  <span className="text-xs text-gray-500">used</span>
                </div>
                <div className="mt-3">
                  <Bar pct={diskPct} warn={70} danger={90} />
                </div>
                <div className="mt-3 text-xs text-gray-500 grid grid-cols-2 gap-2">
                  <span>Used: <strong>{humanBytes(sys.disk?.used_bytes)}</strong></span>
                  <span>Total: <strong>{humanBytes(sys.disk?.total_bytes)}</strong></span>
                  <span>Free: <strong>{humanBytes(sys.disk?.free_bytes)}</strong></span>
                </div>
              </div>
            </div>
          </section>

          {/* MongoDB */}
          <section>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Database className="w-4 h-4" /> MongoDB · <span className="font-mono text-gray-500">{mongo.db_name}</span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <StatCard icon={Database} label="Data" value={humanBytes(mongo.data_bytes)} sub={`${mongo.objects_count?.toLocaleString()} documents`} accent="blue" />
              <StatCard icon={Database} label="Storage" value={humanBytes(mongo.storage_bytes)} sub={`${mongo.collections_count} collections`} accent="purple" />
              <StatCard icon={Database} label="Indexes" value={humanBytes(mongo.index_bytes)} accent="emerald" />
              <StatCard icon={Database} label="Avg Doc Size" value={mongo.objects_count ? humanBytes(Math.round(mongo.data_bytes / mongo.objects_count)) : "—"} accent="amber" />
            </div>
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-semibold text-gray-700">Collection</th>
                    <th className="px-4 py-2.5 text-right font-semibold text-gray-700">Documents</th>
                    <th className="px-4 py-2.5 text-right font-semibold text-gray-700">Data Size</th>
                    <th className="px-4 py-2.5 text-right font-semibold text-gray-700">Storage Size</th>
                    <th className="px-4 py-2.5 text-right font-semibold text-gray-700">Index Size</th>
                  </tr>
                </thead>
                <tbody>
                  {(mongo.collections || []).map((c) => (
                    <tr key={c.name} className="border-b border-gray-100 last:border-0" data-testid={`coll-${c.name}`}>
                      <td className="px-4 py-2.5 font-mono text-gray-800">{c.name}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700">{c.count.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700">{humanBytes(c.size_bytes)}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700">{humanBytes(c.storage_bytes)}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700">{humanBytes(c.index_bytes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              MongoDB document limit is <strong>16 MB</strong> per document. WiredTiger compresses on disk, so <em>Data Size</em> &gt; <em>Storage Size</em> is normal.
            </p>
          </section>

          {/* PDF Storage */}
          <section>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Cloud className="w-4 h-4" /> PDF Storage
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-testid="storage-r2">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">Cloudflare R2</span>
                  <Badge variant="secondary" className={storage.r2?.enabled ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}>
                    {storage.r2?.enabled ? "ACTIVE" : "DISABLED"}
                  </Badge>
                </div>
                {storage.r2?.enabled ? (
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-gray-500">Bucket</div>
                      <div className="font-mono text-gray-800">{storage.r2.bucket}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Objects</div>
                      <div className="font-heading text-xl font-bold">{storage.r2.count.toLocaleString()}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-xs text-gray-500">Total size</div>
                      <div className="font-heading text-xl font-bold">{humanBytes(storage.r2.bytes)}</div>
                    </div>

                    {/* Last-24h R2 analytics */}
                    {r2Metrics?.available ? (
                      <div className="col-span-2 mt-3 pt-3 border-t border-gray-100">
                        <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Last 24 hours</div>
                        <div className="grid grid-cols-2 gap-3 mb-2">
                          <div>
                            <div className="text-xs text-gray-500">Requests</div>
                            <div className="font-heading text-lg font-bold">{(r2Metrics.total_requests || 0).toLocaleString()}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">Bandwidth</div>
                            <div className="font-heading text-lg font-bold">{humanBytes(r2Metrics.total_bytes || 0)}</div>
                          </div>
                        </div>
                        {r2Metrics.by_action?.length > 0 && (
                          <div className="space-y-1">
                            {r2Metrics.by_action.slice(0, 5).map((a) => (
                              <div key={a.action} className="flex justify-between text-xs text-gray-600">
                                <span className="font-mono">{a.action}</span>
                                <span>{a.requests.toLocaleString()} req · {humanBytes(a.bytes)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="col-span-2 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                        <strong>Analytics:</strong> {r2Metrics?.reason || "Loading..."}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mt-3">
                    R2 is not configured in this environment. Set <code className="text-xs bg-gray-100 px-1 rounded">R2_*</code> env vars to enable durable storage.
                  </p>
                )}
              </div>

              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-testid="storage-local">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">Local Disk Cache</span>
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                    {storage.primary === "local" ? "PRIMARY" : "CACHE"}
                  </Badge>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-gray-500">Files</div>
                    <div className="font-heading text-xl font-bold">{storage.local?.count?.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Size</div>
                    <div className="font-heading text-xl font-bold">{humanBytes(storage.local?.bytes)}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-xs text-gray-500">Path</div>
                    <div className="font-mono text-xs text-gray-700 break-all">{storage.local?.path}</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Process */}
          <section>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Backend Process</h2>
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-xs text-gray-500">PID</div>
                  <div className="font-mono font-semibold text-gray-800">{proc.pid}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">RSS Memory</div>
                  <div className="font-semibold text-gray-800">{humanBytes(proc.rss_bytes)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Threads</div>
                  <div className="font-semibold text-gray-800">{proc.threads}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Open Connections</div>
                  <div className="font-semibold text-gray-800">{proc.open_connections ?? "—"}</div>
                </div>
                <div className="col-span-2 md:col-span-4">
                  <div className="text-xs text-gray-500">Started</div>
                  <div className="font-mono text-xs text-gray-700">{proc.started_at}</div>
                </div>
              </div>
            </div>
          </section>

          {/* Data Maintenance — eng # re-scan */}
          <section data-testid="rescan-section">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Data Maintenance</h2>
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <ScanSearch className="w-5 h-5 text-indigo-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-base">Re-scan Engineering Drawing Numbers</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Re-runs the tightened title-block extractor against every existing Hilti extraction and cleans up any
                      spurious eng # tags captured by the old loose fallback. Also updates dependent batch rows so search
                      and the &quot;Secondary Drawings&quot; hint stop referencing wrong parent drawings.
                    </p>
                    <p className="text-xs text-gray-500 mt-1 italic">
                      Start with <strong>Preview</strong> to see what would change without touching data.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    onClick={() => runRescan(true)}
                    disabled={rescanBusy}
                    data-testid="rescan-preview-btn"
                  >
                    {rescanBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanSearch className="w-4 h-4 mr-2" />}
                    Preview changes
                  </Button>
                  <Button
                    onClick={() => setRescanConfirmOpen(true)}
                    disabled={rescanBusy}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="rescan-apply-btn"
                  >
                    Apply
                  </Button>
                </div>
              </div>

              {rescanResult && (
                <div className="mt-5 border-t border-gray-100 pt-4" data-testid="rescan-result">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="secondary" className={rescanResult.dry_run ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}>
                      {rescanResult.dry_run ? "PREVIEW" : "APPLIED"}
                    </Badge>
                    <span className="text-sm text-gray-700">
                      Scanned <strong>{rescanResult.total_extractions_scanned}</strong> extraction(s), {" "}
                      <strong>{rescanResult.total_hilti_tables_seen}</strong> Hilti table(s). {" "}
                      <strong>{rescanResult.total_changed}</strong> eng # would {rescanResult.dry_run ? "change" : "have changed"} ({rescanResult.total_cleared} cleared, {rescanResult.total_corrected} corrected).
                    </span>
                  </div>
                  {rescanResult.per_extraction.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No changes — all existing eng #s already match the strict title-block pattern. </p>
                  ) : (
                    <div className="border border-gray-200 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr className="text-left">
                            <th className="px-3 py-2 font-semibold text-gray-700">Extraction</th>
                            <th className="px-3 py-2 font-semibold text-gray-700">Page</th>
                            <th className="px-3 py-2 font-semibold text-gray-700">PF #</th>
                            <th className="px-3 py-2 font-semibold text-gray-700">Old Eng #</th>
                            <th className="px-3 py-2 font-semibold text-gray-700">→ New Eng #</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rescanResult.per_extraction.flatMap((ext) =>
                            ext.changes.map((c, i) => (
                              <tr key={`${ext.extraction_id}-${c.table_id}`} className="border-t border-gray-100 hover:bg-gray-50">
                                <td className="px-3 py-2 text-gray-700 font-mono truncate max-w-[220px]" title={ext.filename}>{i === 0 ? (ext.filename || ext.extraction_id.slice(0, 8)) : ""}</td>
                                <td className="px-3 py-2 text-gray-600">{c.page_number}</td>
                                <td className="px-3 py-2 font-mono text-gray-700">{c.drawing_number || "—"}</td>
                                <td className="px-3 py-2 font-mono text-red-600 line-through">{c.old || "(empty)"}</td>
                                <td className="px-3 py-2 font-mono text-emerald-700 font-semibold">{c.new || "(cleared)"}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {rescanConfirmOpen && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="rescan-confirm-modal">
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                <h3 className="font-heading font-bold text-lg text-gray-900 mb-2">Apply eng # re-scan?</h3>
                <p className="text-sm text-gray-600 mb-4">
                  This will overwrite <span className="font-semibold">eng_drawing_number</span> on every Hilti extraction and cascade the change into all dependent batch rows. Existing batches will show the new values immediately.
                </p>
                <p className="text-xs text-gray-500 mb-5">
                  Recommended: run <span className="font-medium">Preview changes</span> first.
                </p>
                <div className="flex items-center justify-end gap-2">
                  <Button variant="outline" onClick={() => setRescanConfirmOpen(false)} disabled={rescanBusy} data-testid="rescan-cancel-btn">
                    Cancel
                  </Button>
                  <Button
                    onClick={() => runRescan(false)}
                    disabled={rescanBusy}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="rescan-confirm-btn"
                  >
                    {rescanBusy ? "Applying…" : "Apply now"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Orphan Extractions */}
          <section data-testid="orphan-section">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Orphan Extractions</h2>
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <FileWarning className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-base">Extractions without a batch</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Uploaded PDFs that no batch (Saved Batch) is using. Safe to delete — they only inflate storage and
                      trigger spurious &quot;Shared S-number&quot; merge prompts.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <select
                    value={orphanFilterDays}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setOrphanFilterDays(v);
                      fetchOrphans(v);
                    }}
                    className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white text-gray-700"
                    data-testid="orphan-filter-days"
                  >
                    <option value={0}>Any age</option>
                    <option value={1}>Older than 1 day</option>
                    <option value={7}>Older than 7 days</option>
                    <option value={14}>Older than 14 days</option>
                    <option value={30}>Older than 30 days</option>
                  </select>
                  <Button
                    variant="outline"
                    onClick={() => fetchOrphans()}
                    disabled={orphansLoading}
                    data-testid="orphan-refresh-btn"
                  >
                    {orphansLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    {orphans.length === 0 && !orphansLoading ? "Scan" : "Refresh"}
                  </Button>
                  <Button
                    onClick={purgeSelectedOrphans}
                    disabled={orphanPurging || orphanSelected.size === 0}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="orphan-purge-btn"
                  >
                    {orphanPurging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Delete selected ({orphanSelected.size})
                  </Button>
                </div>
              </div>

              {orphans.length === 0 ? (
                <div className="text-sm text-gray-500 italic py-6 text-center border border-dashed border-gray-200 rounded-lg">
                  {orphansLoading ? "Scanning…" : "No orphan extractions found. "}
                  {!orphansLoading && (
                    <span className="text-gray-400"> Click Scan to check again.</span>
                  )}
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs" data-testid="orphan-table">
                    <thead className="bg-gray-50">
                      <tr className="text-left">
                        <th className="px-3 py-2 w-8">
                          <input
                            type="checkbox"
                            checked={orphanSelected.size === orphans.length && orphans.length > 0}
                            onChange={toggleOrphanSelectAll}
                            data-testid="orphan-select-all"
                            className="w-4 h-4 accent-amber-600"
                          />
                        </th>
                        <th className="px-3 py-2 font-semibold text-gray-700">Filename</th>
                        <th className="px-3 py-2 font-semibold text-gray-700">Format</th>
                        <th className="px-3 py-2 font-semibold text-gray-700">Pages</th>
                        <th className="px-3 py-2 font-semibold text-gray-700">Uploaded</th>
                        <th className="px-3 py-2 font-semibold text-gray-700">View</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orphans.map((o) => (
                        <tr
                          key={o.extraction_id}
                          className={`border-t border-gray-100 hover:bg-gray-50 ${orphanSelected.has(o.extraction_id) ? "bg-amber-50" : ""}`}
                        >
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={orphanSelected.has(o.extraction_id)}
                              onChange={() => toggleOrphanSelect(o.extraction_id)}
                              data-testid={`orphan-select-${o.extraction_id}`}
                              className="w-4 h-4 accent-amber-600"
                            />
                          </td>
                          <td className="px-3 py-2 font-mono text-gray-800 truncate max-w-[320px]" title={o.filename}>{o.filename || "(no filename)"}</td>
                          <td className="px-3 py-2 uppercase text-gray-500">{o.format || "—"}</td>
                          <td className="px-3 py-2 text-gray-700">{o.page_count || "—"}</td>
                          <td className="px-3 py-2 text-gray-600">{o.created_at ? new Date(o.created_at).toLocaleString() : "—"}</td>
                          <td className="px-3 py-2">
                            <a
                              href={`/results/${o.extraction_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                            >
                              <ExternalLink className="w-3 h-3" /> Open
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>

          {/* Time-series chart */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Last {historyHours}h</h2>
              <div className="flex items-center gap-2">
                {[1, 6, 24].map(h => (
                  <Button
                    key={h}
                    size="sm"
                    variant={historyHours === h ? "default" : "outline"}
                    onClick={() => setHistoryHours(h)}
                    data-testid={`history-${h}h`}
                    className="h-7 px-3 text-xs"
                  >
                    {h}h
                  </Button>
                ))}
              </div>
            </div>
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
              {chartData.length === 0 ? (
                <div className="text-center text-gray-400 text-sm py-12">
                  Collecting samples… (one every 5 minutes). Come back in a few minutes to see the trend.
                </div>
              ) : (
                <div style={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 5, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="time" stroke="#94a3b8" fontSize={11} interval="preserveStartEnd" />
                      <YAxis yAxisId="left" stroke="#94a3b8" fontSize={11} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                      <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `${v}MB`} />
                      <Tooltip
                        formatter={(value, name) => {
                          if (name === "DB Size") return [`${value} MB`, name];
                          return [`${value}%`, name];
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line yAxisId="left" type="monotone" dataKey="cpu"  name="CPU"     stroke="#3b82f6" strokeWidth={2} dot={false} />
                      <Line yAxisId="left" type="monotone" dataKey="mem"  name="Memory"  stroke="#8b5cf6" strokeWidth={2} dot={false} />
                      <Line yAxisId="left" type="monotone" dataKey="disk" name="Disk"    stroke="#f59e0b" strokeWidth={2} dot={false} />
                      <Line yAxisId="right" type="monotone" dataKey="db_mb" name="DB Size" stroke="#10b981" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </section>

          {/* Documents in DB */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                <FileText className="w-4 h-4" /> Documents in Database
              </h2>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={fetchDocuments} disabled={docsLoading}>
                  {docsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                </Button>
                {selectedIds.size > 0 && (
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={bulkDelete}
                    disabled={bulkDeleting}
                    data-testid="bulk-delete-btn"
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-2" />}
                    Delete {selectedIds.size} selected
                  </Button>
                )}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {documents.length === 0 ? (
                <div className="text-center text-gray-400 text-sm py-12">
                  No extraction documents in the database.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-2.5 w-10">
                        <input
                          type="checkbox"
                          checked={selectedIds.size > 0 && selectedIds.size === documents.length}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 accent-red-600 cursor-pointer"
                          data-testid="select-all-docs"
                        />
                      </th>
                      <th className="px-4 py-2.5 text-left font-semibold text-gray-700">Filename</th>
                      <th className="px-4 py-2.5 text-left font-semibold text-gray-700">Format</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-gray-700">PDF Size</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-gray-700">Pages</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-gray-700">Tables</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-gray-700">Batches</th>
                      <th className="px-4 py-2.5 text-center font-semibold text-gray-700">PDF</th>
                      <th className="px-4 py-2.5 text-left font-semibold text-gray-700">Created</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map(d => (
                      <tr
                        key={d.id}
                        className={`border-b border-gray-100 last:border-0 ${selectedIds.has(d.id) ? 'bg-red-50/40' : 'hover:bg-gray-50'}`}
                        data-testid={`doc-row-${d.id}`}
                      >
                        <td className="px-4 py-2.5">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(d.id)}
                            onChange={() => toggleSelect(d.id)}
                            className="w-4 h-4 accent-red-600 cursor-pointer"
                            data-testid={`select-doc-${d.id}`}
                          />
                        </td>
                        <td className="px-4 py-2.5 text-gray-800 font-medium max-w-xs truncate" title={d.filename}>{d.filename}</td>
                        <td className="px-4 py-2.5">
                          <Badge variant="secondary" className={d.format === 'hilti' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}>
                            {d.format}
                          </Badge>
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-700">{humanBytes(d.file_size)}</td>
                        <td className="px-4 py-2.5 text-right text-gray-700">{d.page_count}</td>
                        <td className="px-4 py-2.5 text-right text-gray-700">{d.table_count}</td>
                        <td className="px-4 py-2.5 text-right text-gray-700">{d.batch_count}</td>
                        <td className="px-4 py-2.5 text-center">
                          {d.pdf_in_r2 && <Badge className="bg-emerald-100 text-emerald-700 mr-1">R2</Badge>}
                          {d.pdf_on_disk && <Badge className="bg-blue-100 text-blue-700">disk</Badge>}
                          {!d.pdf_in_r2 && !d.pdf_on_disk && <Badge className="bg-gray-100 text-gray-500">none</Badge>}
                        </td>
                        <td className="px-4 py-2.5 text-gray-500 text-xs font-mono">{(d.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                        <td className="px-4 py-2.5 text-right">
                          <Button size="sm" variant="outline" onClick={() => downloadDoc(d)} className="h-7 px-2" data-testid={`dl-${d.id}`}>
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          {/* Alerts */}
          <section>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Bell className="w-4 h-4" /> Threshold Alerts (Webhook)
            </h2>
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Enable alerts</div>
                  <div className="text-xs text-gray-500">When any metric exceeds its threshold for 5 minutes, POST a JSON payload to the URL below.</div>
                </div>
                <Switch
                  checked={alertCfg.enabled}
                  onCheckedChange={(checked) => setAlertCfg(p => ({ ...p, enabled: checked }))}
                  data-testid="alerts-enabled-switch"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="md:col-span-4">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Webhook URL</label>
                  <Input
                    type="url"
                    placeholder="https://hooks.slack.com/services/..., https://your-api.example/alert, etc."
                    value={alertCfg.webhook_url}
                    onChange={(e) => setAlertCfg(p => ({ ...p, webhook_url: e.target.value }))}
                    data-testid="webhook-url-input"
                  />
                </div>
                {['cpu_pct', 'mem_pct', 'disk_pct'].map(k => (
                  <div key={k}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {k === 'cpu_pct' ? 'CPU %' : k === 'mem_pct' ? 'Memory %' : 'Disk %'}
                    </label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={alertCfg.thresholds[k]}
                      onChange={(e) => setAlertCfg(p => ({
                        ...p,
                        thresholds: { ...p.thresholds, [k]: Number(e.target.value) },
                      }))}
                      data-testid={`threshold-${k}`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                <Button variant="outline" onClick={testAlertWebhook} disabled={testingAlert || !alertCfg.webhook_url}>
                  {testingAlert ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send test
                </Button>
                <Button onClick={saveAlertConfig} disabled={savingAlerts}>
                  {savingAlerts ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save settings
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Sample webhook payload: <code className="bg-gray-100 px-1 py-0.5 rounded text-[11px]">{"{\"type\":\"threshold_exceeded\",\"metric\":\"cpu_pct\",\"value\":92.4,\"threshold\":90,\"fired_at\":\"...\"}"}</code>
                <br />
                Cooldown is 30 minutes per metric to avoid spam. Works with Slack, Discord, Zapier, IFTTT, or any custom endpoint.
              </p>
            </div>
          </section>

          {/* User Activity Log */}
          <section className="bg-white rounded-xl border border-gray-200 shadow-soft overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-indigo-700" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-lg text-gray-900">User Activity Log</h2>
                  <p className="text-xs text-gray-500">
                    Every action logged with timestamp + server duration · retained for 48 h
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={toggleActivityLogging}
                  disabled={activityToggleBusy}
                  className={`inline-flex items-center gap-2 h-8 px-3 rounded-md text-xs font-medium transition-colors ${
                    activityEnabled
                      ? "bg-green-100 text-green-700 hover:bg-green-200 border border-green-200"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200"
                  }`}
                  title={activityEnabled ? "Click to pause logging" : "Click to resume logging"}
                  data-testid="activity-toggle"
                >
                  <span className={`inline-block w-2 h-2 rounded-full ${activityEnabled ? "bg-green-500 animate-pulse" : "bg-gray-400"}`} />
                  Logging {activityEnabled ? "ON" : "OFF"}
                </button>
                <input
                  type="text"
                  value={activityUserFilter}
                  onChange={(e) => setActivityUserFilter(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") fetchActivity(activityUserFilter); }}
                  placeholder="Filter by user…"
                  className="h-8 px-2 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  data-testid="activity-user-filter"
                />
                <Button size="sm" variant="outline" onClick={() => fetchActivity(activityUserFilter)} disabled={activityLoading}>
                  {activityLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                  Refresh
                </Button>
              </div>
            </div>
            {/* Latency history chart — hourly averages, last 24 h */}
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-heading font-semibold text-sm text-gray-800">Latency by hour (24h)</h3>
                  <p className="text-[11px] text-gray-500">Server = backend handling · Client = full round-trip (includes network)</p>
                </div>
                <div className="text-[11px] text-gray-500 flex items-center gap-3">
                  <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-indigo-500" /> Server</span>
                  <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-rose-500" /> Client</span>
                </div>
              </div>
              <div className="h-40">
                <ResponsiveContainer>
                  <LineChart data={latencyHistory} margin={{ top: 5, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6b7280" }} />
                    <YAxis unit="ms" tick={{ fontSize: 10, fill: "#6b7280" }} />
                    <Tooltip
                      contentStyle={{ fontSize: "12px", borderRadius: 6 }}
                      formatter={(v, name) => v == null ? ["—", name] : [`${Math.round(v)} ms`, name]}
                    />
                    <Line type="monotone" dataKey="server_ms" name="Server" stroke="#6366f1" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="client_ms" name="Client" stroke="#f43f5e" strokeWidth={2} dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="max-h-[480px] overflow-y-auto">
              <table className="w-full text-xs" data-testid="activity-table">
                <thead className="sticky top-0 bg-indigo-50 z-10">
                  <tr className="text-left text-indigo-800 border-b border-indigo-200">
                    <th className="px-3 py-2 font-semibold whitespace-nowrap">Time</th>
                    <th className="px-3 py-2 font-semibold">User</th>
                    <th className="px-3 py-2 font-semibold">Action</th>
                    <th className="px-3 py-2 font-semibold text-right whitespace-nowrap">Server</th>
                    <th className="px-3 py-2 font-semibold text-right whitespace-nowrap">Client</th>
                    <th className="px-3 py-2 font-semibold text-center">Status</th>
                    <th className="px-3 py-2 font-semibold text-gray-500">Path</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.map((it) => {
                    const ts = new Date(it.timestamp);
                    const timeStr = ts.toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
                    const slow = (it.duration_ms || 0) > 500;
                    const clientSlow = (it.client_duration_ms || 0) > 1500;
                    const err = it.status_code >= 400;
                    return (
                      <tr key={it.id} className={`border-b border-gray-100 ${err ? "bg-red-50/40" : "hover:bg-indigo-50/30"} transition-colors`}>
                        <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap font-mono">{timeStr}</td>
                        <td className="px-3 py-1.5 font-medium text-gray-800 whitespace-nowrap">{it.username || "anonymous"}</td>
                        <td className="px-3 py-1.5 text-gray-700">{it.action}</td>
                        <td className={`px-3 py-1.5 text-right font-mono whitespace-nowrap ${slow ? "text-amber-700 font-semibold" : "text-gray-600"}`}>
                          {it.duration_ms} ms
                        </td>
                        <td className={`px-3 py-1.5 text-right font-mono whitespace-nowrap ${clientSlow ? "text-red-700 font-semibold" : "text-gray-600"}`}>
                          {it.client_duration_ms != null ? `${it.client_duration_ms} ms` : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono ${
                            err ? "bg-red-100 text-red-700"
                              : it.status_code >= 300 ? "bg-amber-100 text-amber-700"
                              : "bg-green-100 text-green-700"
                          }`}>
                            {it.status_code}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-gray-400 truncate max-w-[240px] font-mono" title={`${it.method} ${it.path}`}>{it.method} {it.path}</td>
                      </tr>
                    );
                  })}
                  {!activityLoading && activity.length === 0 && (
                    <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500">No activity yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <p className="text-xs text-gray-400 text-center">
            Snapshot generated {new Date(stats.generated_at).toLocaleString()}
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminStatsPage;
